import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { apiErrorMessage } from '../../utils/apiError';
import {
  ChurchCreateModal,
  ChurchEditModal,
  ChurchMembersModal,
  ChurchDeleteModal
} from './AdminChurchModals';
import {
  IoSearchOutline,
  IoAddOutline,
  IoCreateOutline,
  IoSettingsOutline,
  IoTrashOutline
} from 'react-icons/io5';
import AdminDataTable, { AdminPill } from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import {
  useBulkAction, useRowSelection, runBulk, summarizeBulk, useDebouncedValue, useLatestRequest, useFirstLoad
} from '../../components/admin/useAdminTable';
import './AdminChurchesPage.css';

const AdminChurchesPage = () => {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);

  const [creating, setCreating] = useState(false);
  const [editingChurch, setEditingChurch] = useState(null);
  const [managingChurch, setManagingChurch] = useState(null);
  const [deletingChurch, setDeletingChurch] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const debouncedSearch = useDebouncedValue(search);
  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);

  const loadChurches = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      const response = await adminApi.getChurches({
        page: currentPage,
        limit: 20,
        search: debouncedSearch,
        sortBy,
        sortOrder
      });
      if (!request.isCurrent()) return;
      setChurches(response.data.churches || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(apiErrorMessage(err, 'Failed to load churches.'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => { loadChurches(); }, [loadChurches]);

  const closeModals = () => {
    setCreating(false);
    setEditingChurch(null);
    setManagingChurch(null);
    setDeletingChurch(null);
  };

  const handleModalSaved = (message) => {
    closeModals();
    setSuccessMessage(message);
    loadChurches();
  };

  const formatDate = (value) => new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const selection = useRowSelection(`${currentPage}|${debouncedSearch}|${sortBy}|${sortOrder}`);
  const bulk = useBulkAction((_result, text) => {
    selection.clear();
    setSuccessMessage(text);
    loadChurches();
  });

  const handleSortChange = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  const askChurchBulk = (rows, active) => {
    const wanted = rows.filter((row) => active ? row.isActive === false : row.isActive !== false);
    if (wanted.length === 0) {
      setSuccessMessage(active ? 'None of the selected churches are inactive.' : 'None of the selected churches are active.');
      return;
    }
    bulk.ask({
      title: `${active ? 'Reactivate' : 'Deactivate'} selected churches`,
      confirmLabel: active ? 'Reactivate' : 'Deactivate',
      danger: !active,
      body: (
        <p className="admin-modal-hint">
          {active
            ? 'These churches will show as active again.'
            : 'Inactive churches stay in the data. Members are not removed.'}{' '}
          This applies to {wanted.length} church{wanted.length === 1 ? '' : 'es'}.
        </p>
      ),
      run: () => runBulk(wanted, (row) => adminApi.patch(`/admin/churches/${row.id}`, { isActive: active })),
      summarize: (result) => summarizeBulk(active ? 'Reactivated' : 'Deactivated', 'church', result, 'churches')
    });
  };

  if (firstLoad) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading churches..." />
      </div>
    );
  }

  return (
    <div className="admin-churches-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Churches</h1>
          <p className="admin-page-description">
            Manage churches, approve join requests, and move members on their behalf
          </p>
        </div>
        <button className="church-new-btn" onClick={() => setCreating(true)}>
          <IoAddOutline /> New Church
        </button>
      </div>

      {successMessage && (
        <div className="church-success-banner">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      <div className="admin-card">
        <div className="churches-controls">
          <div className="search-box">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              placeholder="Search churches by name or join code..."
              value={search}
              onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }}
              className="search-input"
            />
          </div>

          {/* Column headers sort on desktop; cards on narrow screens have no headers */}
          <div className="sort-controls admin-sort-mobile">
            <label>Sort by:</label>
            <select
              value={sortBy}
              onChange={(event) => handleSortChange(event.target.value)}
              className="sort-select"
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="pendingMembersCount">Pending Requests</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className="sort-order-btn"
            >
              {sortOrder === 'ASC' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      <AdminDataTable
        title={`Churches (${pagination.totalChurches || 0})`}
        rows={churches}
        getRowLabel={(church) => church.name}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={loadChurches}
        emptyTitle="No churches found"
        emptyMessage="No churches match your search criteria."
        sort={{ key: sortBy, order: sortOrder, onChange: handleSortChange }}
        pagination={{
          page: currentPage,
          totalPages: pagination.totalPages,
          hasPrev: pagination.hasPrevPage,
          hasNext: pagination.hasNextPage,
          onPageChange: setCurrentPage
        }}
        bulkActions={[
          { label: 'Deactivate', onClick: (rows) => askChurchBulk(rows, false) },
          { label: 'Reactivate', danger: false, onClick: (rows) => askChurchBulk(rows, true) }
        ]}
        columns={[
          {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            render: (church) => (
              <span className="admin-cell-strong">
                {church.name}
                {church.isActive === false && <span className="admin-cell-sub"><AdminPill tone="danger">Inactive</AdminPill></span>}
              </span>
            )
          },
          { key: 'approved', header: 'Approved', render: (church) => church.approvedCount },
          {
            key: 'pending',
            header: 'Pending',
            sortKey: 'pendingMembersCount',
            render: (church) => church.pendingCount > 0
              ? <AdminPill tone="warn">{church.pendingCount}</AdminPill>
              : 0
          },
          { key: 'readers', header: 'Readers', render: (church) => church.readerCount },
          { key: 'code', header: 'Join code', render: (church) => church.joinCode },
          { key: 'admin', header: 'Administered by', render: (church) => church.admin?.email || 'unknown' },
          { key: 'created', header: 'Created', sortKey: 'createdAt', render: (church) => formatDate(church.createdAt) }
        ]}
        renderActions={(church) => (
          <>
            <button type="button" onClick={() => setManagingChurch(church)} title="Members and approvals">
              <IoSettingsOutline /> Members
              {church.pendingCount > 0 && <AdminPill tone="warn">{church.pendingCount}</AdminPill>}
            </button>
            <button type="button" onClick={() => setEditingChurch(church)} title="Edit church">
              <IoCreateOutline /> Edit
            </button>
            <button type="button" className="is-danger" onClick={() => setDeletingChurch(church)} title="Delete church">
              <IoTrashOutline /> Delete
            </button>
          </>
        )}
      />
      <BulkActionHost bulk={bulk} />

      {creating && (
        <ChurchCreateModal onClose={closeModals} onSaved={handleModalSaved} />
      )}

      {editingChurch && (
        <ChurchEditModal church={editingChurch} onClose={closeModals} onSaved={handleModalSaved} />
      )}

      {managingChurch && (
        <ChurchMembersModal
          church={managingChurch}
          onClose={closeModals}
          onChanged={loadChurches}
        />
      )}

      {deletingChurch && (
        <ChurchDeleteModal church={deletingChurch} onClose={closeModals} onDeleted={handleModalSaved} />
      )}
    </div>
  );
};

export default AdminChurchesPage;
